import { useEffect, useRef, useMemo, useCallback } from 'react';
import { useLazyGetBundlePageInfoQuery } from '@/api';
import {
  selectCachedPageIds,
  selectSharedDataActiveBundleType,
  selectSharedDataBundleId,
  mergePageInfo,
  useAppDispatch,
  useAppSelector,
  PageInfoData,
} from '@/store';
import { debounce } from '@/utils';

const PREFETCH_DEBOUNCE_MS = 300;
const LOOKAHEAD_WINDOW_SIZE = 50;
const PAGE_LOAD_BUFFER_SIZE = 200;

interface UseThumbnailPageInfoPrefetchProps {
  visiblePageIds: string[];
  allItemIds: string[];
}

/**
 * Hook that prefetches page info (highRelevance, inLightBundle, etc.)
 * for visible thumbnails as user scrolls.
 */
export const useThumbnailPageInfoPrefetch = ({ visiblePageIds, allItemIds }: UseThumbnailPageInfoPrefetchProps) => {
  const dispatch = useAppDispatch();
  const bundleId = useAppSelector(selectSharedDataBundleId);
  const activeBundleType = useAppSelector(selectSharedDataActiveBundleType);
  const cachedPageIds = useAppSelector(selectCachedPageIds);
  const [triggerGetBundlePageInfo] = useLazyGetBundlePageInfoQuery();

  // Track which pageIds we've already requested to avoid duplicate calls
  const requestedPageIdsRef = useRef<Set<string>>(new Set());

  const fetchPageInfo = useCallback(
    async (pageIdsToFetch: string[]) => {
      if (!bundleId || !activeBundleType || pageIdsToFetch.length === 0) {
        return;
      }

      // Mark as requested
      pageIdsToFetch.forEach((id) => requestedPageIdsRef.current.add(id));

      try {
        const result = await triggerGetBundlePageInfo({
          bundleId,
          pageIds: pageIdsToFetch,
        }).unwrap();

        if (result.pageInfo) {
          // Transform the response to our store format
          const pageInfoData: Record<string, PageInfoData> = {};
          Object.entries(result.pageInfo).forEach(([pageId, info]) => {
            pageInfoData[pageId] = {
              highRelevance: info.highRelevance,
              inLightBundle: info.inLightBundle,
              bookmarkDate: info.bookmarkDate,
              clusterDate: info.clusterDate,
              ...(info.chronologyEntryIds !== undefined && { chronologyEntryIds: info.chronologyEntryIds }),
              dates: info.dates,
              textLabels: info.textLabels,
            };
          });

          dispatch(
            mergePageInfo({
              key: activeBundleType,
              data: pageInfoData,
            }),
          );
        }
      } catch (error) {
        // Remove from requested set so we can retry
        pageIdsToFetch.forEach((id) => requestedPageIdsRef.current.delete(id));
        console.error('[PageInfoPrefetch] Error fetching page info:', error);
      }
    },
    [bundleId, activeBundleType, triggerGetBundlePageInfo, dispatch],
  );

  const debouncedFetchPageInfo = useMemo(() => debounce(fetchPageInfo, PREFETCH_DEBOUNCE_MS), [fetchPageInfo]);

  // Pre-build an O(1) lookup of pageId → index so the prefetch effect doesn't
  // do an O(allItemIds) `indexOf` per visible page on every scroll tick.
  const itemIdToIndex = useMemo(() => {
    const map = new Map<string, number>();
    allItemIds.forEach((id, idx) => map.set(id, idx));
    return map;
  }, [allItemIds]);

  useEffect(() => {
    if (!bundleId || !activeBundleType || visiblePageIds.length === 0) {
      return;
    }

    // check a buffer window of 50 pages around the visible thumbnails
    let minVisibleIndex = 0;
    let maxVisibleIndex = 0;
    visiblePageIds.forEach((pageId) => {
      const pageIndex = itemIdToIndex.get(pageId) ?? -1;
      if (pageIndex < 0) return;
      minVisibleIndex = minVisibleIndex === 0 ? pageIndex : Math.min(minVisibleIndex, pageIndex);
      maxVisibleIndex = Math.max(maxVisibleIndex, pageIndex);
    });

    // see if any pages in the buffer window are uncached
    const bufferPages = allItemIds.slice(
      Math.max(0, minVisibleIndex - LOOKAHEAD_WINDOW_SIZE),
      Math.min(allItemIds.length, maxVisibleIndex + LOOKAHEAD_WINDOW_SIZE),
    );
    const uncachedBufferPages = bufferPages.filter(
      (pageId) => !cachedPageIds.has(pageId) && !requestedPageIdsRef.current.has(pageId),
    );

    // if so, load the next batch
    if (uncachedBufferPages.length > 0) {
      const requestPageBatch = allItemIds.slice(
        Math.max(0, minVisibleIndex - PAGE_LOAD_BUFFER_SIZE),
        Math.min(allItemIds.length, maxVisibleIndex + PAGE_LOAD_BUFFER_SIZE),
      );
      // Filter out pageIds that are already cached or already requested
      const pageIdsToFetch = requestPageBatch.filter(
        (pageId) => !cachedPageIds.has(pageId) && !requestedPageIdsRef.current.has(pageId),
      );

      if (pageIdsToFetch.length > 0) {
        debouncedFetchPageInfo(pageIdsToFetch);
      }
    }
  }, [visiblePageIds, allItemIds, itemIdToIndex, cachedPageIds, bundleId, activeBundleType, debouncedFetchPageInfo]);

  // Clear requested set when bundle changes
  useEffect(() => {
    requestedPageIdsRef.current = new Set();
  }, [bundleId]);
};
